import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import { useTranslation } from 'react-i18next';
import KPIListComponent from '../../../../PowertrainAssetDetails/CrossAssetAnalysis/CrossAssetAnalysisSidepanel/AssetList/KPIListComponent';
import { CHART_MAX_KPI_LIMIT } from '../../../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView/constants';
import { SearchBox } from 'sharedComponents/SearchBox';
import { getAssetIcon } from 'helpers/icons';
import * as crossAssetAnalysisConstants from '../../constants';

const { scatterHistogramAssistCardTypes } = crossAssetAnalysisConstants;

const SignalPickerComponent = ({
    selectedAsset,
    selectedAssetCapability,
    kpiListLoadingStatus,
    kpiList,
    chartData,
    activeTab,
    selectedKPIType,
    onAddSameSignals,
    onSelectSignal,
    onDeselectSignal,
    isSignalSelected,
    sameSignalsCount,
    lockedSignalKey,
    lockedSignalName,
    scatterHistogramAssistCard,
    onAddLockedSignal
}) => {
    const { t: translate } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const capabilityLoadingStatus = selectedAssetCapability?.loadingStatus;
    const isAtLimit = chartData?.length >= CHART_MAX_KPI_LIMIT;

    if (!selectedAsset) {
        return (
            <div className='signal-picker signal-picker--empty'>
                <span className='signal-picker-empty-msg'>
                    {translate('ABB.Powertrain.Frontend.crossAssetSelectAssetToViewSignals')}
                </span>
            </div>
        );
    }

    return (
        <div className='signal-picker'>
            <div className='signal-picker-asset-header'>
                <span className='signal-picker-asset-icon'>
                    {getAssetIcon(selectedAsset.componentTypeName, { width: 16, height: 16 })}
                </span>
                <div className='signal-picker-asset-header-text'>
                    <span className='signal-picker-asset-header-title'>
                        {translate('ABB.Powertrain.Frontend.crossAssetSignalsFromAsset') || 'Signals from'}
                    </span>
                    <Tippy
                        content={selectedAsset.name || selectedAsset.componentName}
                        animation='scale'
                        theme='light-border'
                        trigger='mouseenter'
                        placement='top'
                        maxWidth={280}
                        onShow={({ reference }) => reference.scrollWidth > reference.offsetWidth}
                    >
                        <span className='signal-picker-asset-name'>
                            {selectedAsset.name || selectedAsset.componentName}
                        </span>
                    </Tippy>
                </div>
            </div>

            {/* Scatter/Histogram assist cards (replace Trend card for those modes) */}
            {scatterHistogramAssistCard?.type === scatterHistogramAssistCardTypes.ADD &&
                <div className='signal-picker-suggestion'>
                    <Tippy
                        content={translate('ABB.Powertrain.Frontend.crossAssetScatterAddSignal', { signal: scatterHistogramAssistCard.signalName }) ||
                            `Add ${scatterHistogramAssistCard.signalName}`}
                        animation='scale'
                        theme='light-border'
                        trigger='mouseenter'
                        placement='top'
                        maxWidth={280}
                        onShow={({ reference }) => reference.scrollWidth > reference.offsetWidth}
                    >
                        <button
                            className='signal-picker-add-same-btn'
                            type='button'
                            onClick={onAddLockedSignal}
                        >
                            {translate('ABB.Powertrain.Frontend.crossAssetScatterAddSignal', { signal: scatterHistogramAssistCard.signalName }) ||
                                `Add ${scatterHistogramAssistCard.signalName}`}
                        </button>
                    </Tippy>
                    <span className='signal-picker-suggestion-text'>
                        {translate('ABB.Powertrain.Frontend.crossAssetScatterAddSubtext') ||
                            'To compare a different signal, select it in the comparison set, or clear all entries.'}
                    </span>
                </div>
            }
            {scatterHistogramAssistCard?.type === scatterHistogramAssistCardTypes.ALREADY_ADDED &&
                <div className='signal-picker-suggestion signal-picker-suggestion--info'>
                    <span className='signal-picker-suggestion-title'>
                        {translate('ABB.Powertrain.Frontend.crossAssetScatterAlreadyAdded', { signal: scatterHistogramAssistCard.signalName }) ||
                            `${scatterHistogramAssistCard.signalName} is already added`}
                    </span>
                    <span className='signal-picker-suggestion-text'>
                        {translate('ABB.Powertrain.Frontend.crossAssetScatterAlreadyAddedSubtext') ||
                            'To compare a different signal, select it in the comparison set, or clear all entries.'}
                    </span>
                </div>
            }
            {scatterHistogramAssistCard?.type === scatterHistogramAssistCardTypes.CANT_JOIN &&
                <div className='signal-picker-suggestion signal-picker-suggestion--warning'>
                    <span className='signal-picker-suggestion-title'>
                        {translate('ABB.Powertrain.Frontend.crossAssetScatterCantJoin', {
                            assetName: scatterHistogramAssistCard.assetName,
                            assetType: scatterHistogramAssistCard.assetType,
                            lockedType: scatterHistogramAssistCard.lockedAssetType
                        }) || `${scatterHistogramAssistCard.assetName} (${scatterHistogramAssistCard.assetType}) can't join this ${scatterHistogramAssistCard.lockedAssetType} comparison.`}
                    </span>
                    <span className='signal-picker-suggestion-text'>
                        {translate('ABB.Powertrain.Frontend.crossAssetScatterCantJoinSubtext', { assetType: scatterHistogramAssistCard.assetType }) ||
                            `To compare ${scatterHistogramAssistCard.assetType} assets, clear all entries in the comparison set.`}
                    </span>
                </div>
            }

            {/* Trend-only assist card */}
            {!scatterHistogramAssistCard && sameSignalsCount > 0 &&
                <div className='signal-picker-suggestion'>
                    <Tippy
                        content={translate('ABB.Powertrain.Frontend.crossAssetAddSameSignalsBtn', { count: sameSignalsCount }) ||
                            `Add same ${sameSignalsCount} signal(s)`}
                        animation='scale'
                        theme='light-border'
                        trigger='mouseenter'
                        placement='top'
                        maxWidth={280}
                        onShow={({ reference }) => reference.scrollWidth > reference.offsetWidth}
                    >
                        <button
                            className='signal-picker-add-same-btn'
                            type='button'
                            onClick={onAddSameSignals}
                        >
                            {translate('ABB.Powertrain.Frontend.crossAssetAddSameSignalsBtn', { count: sameSignalsCount }) ||
                                `Add same ${sameSignalsCount} signal(s)`}
                        </button>
                    </Tippy>
                    <span className='signal-picker-suggestion-text'>
                        {translate('ABB.Powertrain.Frontend.crossAssetAddSameSignalsSuggestion', {
                            assetName: selectedAsset.name || selectedAsset.componentName,
                            count: sameSignalsCount,
                            assetType: selectedAsset.componentTypeName || ''
                        }) || `${selectedAsset.name || selectedAsset.componentName} can use the same ${sameSignalsCount} signal(s) selected for other ${selectedAsset.componentTypeName || ''} asset(s)`}
                    </span>
                </div>
            }

            <div className='signal-picker-search'>
                <SearchBox
                    placeholder={translate('ABB.Powertrain.Frontend.crossAssetSearchSignalsPlaceholder') || 'Search Signals'}
                    value={searchText}
                    onChange={setSearchText}
                />
            </div>

            {isAtLimit &&
                <div className='signal-picker-limit-msg'>
                    {translate('ABB.Powertrain.Frontend.crossAssetMaxSignalsReached', { max: CHART_MAX_KPI_LIMIT }) ||
                        `Maximum ${CHART_MAX_KPI_LIMIT} signals reached`}
                </div>
            }

            <div className={`signal-picker-kpi-list${lockedSignalKey ? ' signal-picker-kpi-list--locked' : ''}`}>
                <KPIListComponent
                    selectedAsset={selectedAsset}
                    kpiList={kpiList}
                    selectedAssetCapability={selectedAssetCapability || { loadingStatus: kpiListLoadingStatus, data: null }}
                    capabilityLoadingStatus={capabilityLoadingStatus}
                    chartData={chartData}
                    setKpi={onSelectSignal}
                    onSelectSignal={onSelectSignal}
                    onDeselectSignal={onDeselectSignal}
                    isSignalSelected={isSignalSelected}
                    selectedKPIType={selectedKPIType}
                    crossAssetAnalysisActiveTab={activeTab}
                    searchText={searchText}
                    lockedSignalKey={lockedSignalKey}
                    lockedSignalName={lockedSignalName}
                />
            </div>
        </div>
    );
};

SignalPickerComponent.propTypes = {
    selectedAsset: PropTypes.object,
    selectedAssetCapability: PropTypes.object,
    kpiListLoadingStatus: PropTypes.string,
    kpiList: PropTypes.object,
    chartData: PropTypes.array,
    activeTab: PropTypes.string,
    selectedKPIType: PropTypes.string,
    onAddSameSignals: PropTypes.func,
    onSelectSignal: PropTypes.func,
    onDeselectSignal: PropTypes.func,
    isSignalSelected: PropTypes.func,
    sameSignalsCount: PropTypes.number,
    lockedSignalKey: PropTypes.string,
    lockedSignalName: PropTypes.string,
    onAddLockedSignal: PropTypes.func,
    scatterHistogramAssistCard: PropTypes.shape({
        type: PropTypes.string,
        signalName: PropTypes.string,
        assetName: PropTypes.string,
        assetType: PropTypes.string,
        lockedAssetType: PropTypes.string,
    }),
};

export default SignalPickerComponent;
